/**
 * The SAC codes a service business actually uses, and a guess from an item's name.
 *
 * ── WHY A LIST LIVES IN THE PRODUCT ──
 * Every line on a GST tax invoice must carry an HSN (goods) or SAC (services)
 * - four digits below ₹5 crore of turnover, six above (Notification 78/2020 -
 * Central Tax) - and GSTR-1 Table 12 is a summary BY that code. The first
 * migration brought in thirteen items and sixteen invoices with no code on
 * any of them, because the old system printed none, and a person facing an
 * empty "HSN/SAC" box types nothing, or types the one they remember from a
 * friend's invoice. The scheme of classification for services (Annexure to
 * Notification 11/2017 - Central Tax (Rate)) runs to hundreds of codes; the
 * ones a freelancer or a small LLP selling software, design, marketing,
 * training and consulting needs fit on a page, with the wording the tariff
 * uses so the person recognises their own work in it.
 *
 * ── WHY ONLY SERVICES ──
 * A SAC picks a KIND of work, and the kinds are few. An HSN picks a kind of
 * GOODS from a tariff with thousands of headings whose choice turns on the
 * material and the use, and a wrong one changes the rate. Fint will not guess
 * an HSN; for goods the person types the heading their supplier's invoice or
 * their CA gives them. The list is Chapter 99 only.
 *
 * ── WHY THE GUESS IS RULES, NOT A MODEL ──
 * `suggestSac` reads the item's name for the words the tariff headings use:
 * "development" is IT design and development, "hosting" is hosting. An item
 * the words do not place gets NO suggestion rather than the nearest one -
 * the suggestion is shown with its wording and the person confirms it, and
 * a confident wrong code reads exactly like a right one. First match wins,
 * and the rules are ordered from the more specific heading to the broader:
 * "website development and SEO" is development (998314), not advertising.
 *
 * Source: the SAC scheme as published by CBIC. The titles are abridged where
 * the tariff's are a paragraph; the code is what goes on the return.
 */
const G = {
    it: 'IT and software',
    consulting: 'Consulting and professional',
    advertising: 'Advertising and marketing',
    design: 'Design and creative',
    content: 'Online content and media',
    training: 'Education and training',
    finance: 'Accounting, tax and legal',
    support: 'Business support',
    licensing: 'Licensing and rental',
};
export const SAC_CODES = [
    // 99831 - Management consulting; information technology services
    { code: '998311', title: 'Management consulting and management services (financial, strategic, HR, marketing, operations)', group: G.consulting },
    { code: '998312', title: 'Business consulting services, including public relations', group: G.consulting },
    { code: '998313', title: 'Information technology (IT) consulting and support services', group: G.it },
    { code: '998314', title: 'Information technology (IT) design and development services', group: G.it },
    { code: '998315', title: 'Hosting and IT infrastructure provisioning services', group: G.it },
    { code: '998316', title: 'IT infrastructure and network management services', group: G.it },
    { code: '998319', title: 'Other information technology services n.e.c.', group: G.it },
    // 99834 - Scientific and other technical services
    { code: '998346', title: 'Technical testing and analysis services', group: G.consulting },
    // 99836 - Advertising services and provision of advertising space or time
    { code: '998361', title: 'Advertising services', group: G.advertising },
    { code: '998362', title: 'Purchase or sale of advertising space or time, on commission', group: G.advertising },
    { code: '998365', title: 'Sale of internet advertising space', group: G.advertising },
    { code: '998366', title: 'Sale of other advertising space or time (except on commission)', group: G.advertising },
    // 99837 - Market research and public opinion polling
    { code: '998371', title: 'Market research services', group: G.consulting },
    { code: '998372', title: 'Public opinion polling services', group: G.consulting },
    // 99838 - Photography and videography
    { code: '998381', title: 'Portrait photography services', group: G.design },
    { code: '998382', title: 'Advertising and related photography services', group: G.design },
    { code: '998383', title: 'Event photography and event videography services', group: G.design },
    { code: '998387', title: 'Other photography and videography and their processing services', group: G.design },
    // 99839 - Other professional, technical and business services
    { code: '998391', title: 'Specialty design services (interior, fashion, industrial, graphic and other specialty design)', group: G.design },
    { code: '998392', title: 'Design originals', group: G.design },
    { code: '998393', title: 'Scientific and technical consulting services', group: G.consulting },
    { code: '998395', title: 'Translation and interpretation services', group: G.consulting },
    { code: '998397', title: 'Sponsorship services and brand promotion services', group: G.advertising },
    { code: '998399', title: 'Other professional, technical and business services n.e.c.', group: G.consulting },
    // 99843 - On-line content
    { code: '998431', title: 'On-line text based information (online books, newspapers, periodicals, directories)', group: G.content },
    { code: '998432', title: 'On-line audio content', group: G.content },
    { code: '998433', title: 'On-line video content', group: G.content },
    { code: '998434', title: 'Software downloads', group: G.content },
    { code: '998439', title: 'Other on-line contents n.e.c.', group: G.content },
    // 99842 - Internet telecommunications
    { code: '998422', title: 'Internet access services', group: G.it },
    // 99851 / 99859 - Employment and other support services
    { code: '998512', title: 'Permanent placement services', group: G.support },
    { code: '998513', title: 'Contract staffing services', group: G.support },
    { code: '998519', title: 'Other employment and labour supply services n.e.c.', group: G.support },
    { code: '998593', title: 'Telephone-based support services', group: G.support },
    { code: '998594', title: 'Combined office administrative services', group: G.support },
    { code: '998596', title: 'Events, exhibitions, conventions and trade shows organisation and assistance', group: G.support },
    { code: '998599', title: 'Other support services n.e.c.', group: G.support },
    // 99871 - Maintenance and repair
    { code: '998713', title: 'Maintenance and repair services of computers and peripheral equipment', group: G.it },
    { code: '998716', title: 'Maintenance and repair services of telecommunication equipment', group: G.it },
    // 99821 / 99822 / 99823 - Legal, accounting, tax
    { code: '998211', title: 'Legal advisory and representation services (criminal law)', group: G.finance },
    { code: '998212', title: 'Legal advisory and representation services (other fields of law)', group: G.finance },
    { code: '998221', title: 'Financial auditing services', group: G.finance },
    { code: '998222', title: 'Accounting and bookkeeping services', group: G.finance },
    { code: '998223', title: 'Payroll services', group: G.finance },
    { code: '998231', title: 'Corporate tax consulting and preparation services', group: G.finance },
    { code: '998232', title: 'Individual tax preparation and planning services', group: G.finance },
    // 99929 - Other education and training
    { code: '999293', title: 'Commercial training and coaching services', group: G.training },
    { code: '999294', title: 'Other education and training services n.e.c.', group: G.training },
    // 99961 / 99963 - Audiovisual; authors and artists
    { code: '999611', title: 'Sound recording services', group: G.content },
    { code: '999612', title: 'Motion picture, videotape production and distribution services', group: G.content },
    { code: '999613', title: 'Audiovisual production support services', group: G.content },
    { code: '999631', title: 'Services of performing artists (actors, readers, musicians, singers, dancers, TV personalities)', group: G.content },
    { code: '999632', title: 'Services of authors, composers, sculptors and other artists, except performing artists', group: G.content },
    // 99733 / 99731 - Licensing and rental
    { code: '997331', title: 'Licensing services for the right to use computer software and databases', group: G.licensing },
    { code: '997314', title: 'Leasing or rental of office machinery and equipment (including computers) without operator', group: G.licensing },
];
/** The entry for a code, or undefined. Four digits match the heading's first entry. */
export function findSac(code) {
    const digits = (code ?? '').replace(/\D/g, '');
    if (!digits)
        return undefined;
    return SAC_CODES.find((entry) => entry.code === digits) ?? (digits.length === 4 ? SAC_CODES.find((entry) => entry.code.startsWith(digits)) : undefined);
}
/**
 * The heading an item's name suggests, or null. Ordered from the specific to
 * the broad, first match wins - see the header. Word boundaries, so "ads"
 * does not fire on "roads" and "ai" does not fire on "maintenance".
 */
const RULES = [
    [/\b(hosting|hosted|server|servers|cloud|domain|domains|vps|cdn|storage|dns|ssl)\b/i, '998315'],
    [/\b(network|networking|devops|sre|infrastructure management|monitoring)\b/i, '998316'],
    // A licence to use software is not the making of it: before the development rule, which 'software' would fire.
    [/\b(licen[cs]e|licen[cs]es|licensing|subscription|subscriptions)\b/i, '997331'],
    [/\b(develop\w*|software|app|apps|application|applications|website|websites|web|portal|bot|bots|chatbot|automation|integration|api|apis|plugin|plugins|module|modules|implementation|coding|programming|ai|ml|saas|erp|crm|ui|ux|mobile|android|ios|backend|frontend|dashboard|prototype|mvp|migration)\b/i, '998314'],
    [/\b(support|maintenance|amc|retainer|troubleshoot\w*|it consult\w*|tech(nical)? consult\w*|helpdesk)\b/i, '998313'],
    [/\b(seo|sem|marketing|advertis\w*|campaign|campaigns|social media|ads|adwords|promotion|promotions|branding|influencer|lead generation|ppc)\b/i, '998361'],
    [/\b(training|trainings|course|courses|coaching|workshop|workshops|bootcamp|class|classes|tuition|mentoring|lesson|lessons)\b/i, '999293'],
    [/\b(video|videos|videography|film|editing|reel|reels|animation|motion graphics)\b/i, '999612'],
    [/\b(photo|photos|photography|photoshoot|shoot)\b/i, '998387'],
    [/\b(graphic|graphics|logo|logos|illustration|illustrations|brand identity|poster|posters|brochure|packaging design|design|designs)\b/i, '998391'],
    [/\b(content|copywriting|copy|blog|blogs|article|articles|writing|writer|script|scripts|newsletter|ghostwriting)\b/i, '998399'],
    [/\b(recruit\w*|placement|staffing|manpower|hiring)\b/i, '998512'],
    [/\b(accounting|bookkeeping|book-keeping|gst filing|gst return|tds return|itr|tax filing|compliance)\b/i, '998222'],
    [/\b(audit|auditing)\b/i, '998221'],
    [/\b(legal|lawyer|advocate|drafting|agreement|agreements|contract review)\b/i, '998212'],
    [/\b(event|events|exhibition|conference|webinar|summit)\b/i, '998596'],
    [/\b(translation|translate|interpretation|transcription)\b/i, '998395'],
    [/\b(research|survey|surveys|analysis|analytics)\b/i, '998371'],
    [/\b(rent|rental|lease|leasing|hire)\b/i, '997314'],
    [/\b(consult\w*|advisory|advice|strategy|strategic|management|planning|audit readiness|coaching business)\b/i, '998311'],
];
/** The SAC an item's name suggests, with its wording so the person can confirm it - or null when the words place nothing. */
export function suggestSac(name) {
    const text = (name ?? '').trim();
    if (!text)
        return null;
    for (const [pattern, code] of RULES) {
        if (pattern.test(text))
            return findSac(code) ?? null;
    }
    return null;
}
//# sourceMappingURL=hsn-sac.js.map